// @ts-nocheck
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  Target, 
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  Award
} from "lucide-react";

const PartnershipAnalytics: React.FC = () => {
  // Mock analytics data
  const revenueMetrics = {
    totalRevenue: 127000,
    growthRate: 15.3,
    averagePartnerValue: 5292,
    topPerformer: "Team Dealers Network"
  };

  const pipelineMetrics = {
    totalProspects: 24,
    conversionRate: 68,
    averageDealSize: 8500,
    timeToClose: 45
  };

  const partnershipHealth = [
    { name: "National Coaches Association", health: 95, revenue: 25000, type: "Strategic" },
    { name: "Stripe Payment Processing", health: 92, revenue: 18000, type: "Technology" },
    { name: "Team Dealers Network", health: 88, revenue: 32000, type: "Distribution" },
    { name: "MaxPreps Content", health: 85, revenue: 15000, type: "Content" },
    { name: "Hudl Video Analysis", health: 78, revenue: 12000, type: "Technology" }
  ];

  const monthlyTrends = [
    { month: "Jan", revenue: 85000, partnerships: 18, newDeals: 2 },
    { month: "Feb", revenue: 92000, partnerships: 19, newDeals: 1 },
    { month: "Mar", revenue: 108000, partnerships: 21, newDeals: 3 },
    { month: "Apr", revenue: 115000, partnerships: 22, newDeals: 2 },
    { month: "May", revenue: 127000, partnerships: 24, newDeals: 4 }
  ];

  const categoryBreakdown = [
    { category: "Technology", count: 8, revenue: 45000, percentage: 35 },
    { category: "Strategic", count: 6, revenue: 38000, percentage: 30 },
    { category: "Distribution", count: 5, revenue: 32000, percentage: 25 },
    { category: "Content", count: 5, revenue: 12000, percentage: 10 }
  ];

  const getHealthColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Partnership Analytics</h2>
          <p className="text-muted-foreground">Performance insights and partnership health metrics</p>
        </div>
        <div className="flex gap-2">
          <Select defaultValue="last_6_months">
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_30_days">Last 30 Days</SelectItem>
              <SelectItem value="last_3_months">Last 3 Months</SelectItem>
              <SelectItem value="last_6_months">Last 6 Months</SelectItem>
              <SelectItem value="last_year">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold text-foreground">{formatCurrency(revenueMetrics.totalRevenue)}</p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="h-3 w-3 text-green-600" />
                  <span className="text-xs text-green-600">+{revenueMetrics.growthRate}%</span>
                </div>
              </div>
              <div className="p-2 rounded-lg bg-green-100">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Partnerships</p>
                <p className="text-2xl font-bold text-foreground">{pipelineMetrics.totalProspects}</p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="h-3 w-3 text-blue-600" />
                  <span className="text-xs text-blue-600">+3 this month</span>
                </div>
              </div>
              <div className="p-2 rounded-lg bg-blue-100">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversion Rate</p>
                <p className="text-2xl font-bold text-foreground">{pipelineMetrics.conversionRate}%</p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="h-3 w-3 text-purple-600" />
                  <span className="text-xs text-purple-600">+5% vs last quarter</span>
                </div>
              </div>
              <div className="p-2 rounded-lg bg-purple-100">
                <Target className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Time to Close</p>
                <p className="text-2xl font-bold text-foreground">{pipelineMetrics.timeToClose} days</p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingDown className="h-3 w-3 text-orange-600" />
                  <span className="text-xs text-orange-600">-8 days</span>
                </div>
              </div>
              <div className="p-2 rounded-lg bg-orange-100">
                <Calendar className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Revenue Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {monthlyTrends.map((trend, index) => (
                <div key={trend.month} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium w-8">{trend.month}</span>
                    <div className="flex-1">
                      <Progress 
                        value={(trend.revenue / 130000) * 100} 
                        className="h-2 w-32"
                      />
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{formatCurrency(trend.revenue)}</p>
                    <p className="text-xs text-muted-foreground">{trend.partnerships} partnerships</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Partnership Categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {categoryBreakdown.map((category, index) => (
                <div key={category.category} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{category.category}</span>
                    <span className="text-sm text-muted-foreground">
                      {category.count} partnerships
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={category.percentage} className="flex-1 h-2" />
                    <span className="text-sm font-medium w-20 text-right">
                      {formatCurrency(category.revenue)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Partnership Health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Partnership Health Scores
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {partnershipHealth.map((partnership, index) => (
              <div key={partnership.name} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {index === 0 && <Award className="h-4 w-4 text-yellow-500" />}
                    <span className="font-medium">{partnership.name}</span>
                  </div>
                  <span className="text-sm text-muted-foreground px-2 py-1 bg-background rounded">
                    {partnership.type}
                  </span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-sm font-medium">{formatCurrency(partnership.revenue)}</p>
                    <p className="text-xs text-muted-foreground">Monthly revenue</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress value={partnership.health} className="w-20 h-2" />
                    <span className={`text-sm font-medium ${getHealthColor(partnership.health)}`}>
                      {partnership.health}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Items */}
      <Card>
        <CardHeader>
          <CardTitle>Recommended Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="p-1 rounded bg-yellow-100">
                <Target className="h-4 w-4 text-yellow-600" />
              </div>
              <div>
                <p className="font-medium text-foreground">Review Underperforming Partnerships</p>
                <p className="text-sm text-muted-foreground">
                  2 partnerships have health scores below 80%. Schedule review meetings.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="p-1 rounded bg-green-100">
                <TrendingUp className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-foreground">Expand Top Performing Categories</p>
                <p className="text-sm text-muted-foreground">
                  Technology partnerships show 35% higher revenue. Consider expanding this category.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="p-1 rounded bg-blue-100">
                <Calendar className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-foreground">Renewal Planning</p>
                <p className="text-sm text-muted-foreground">
                  3 partnerships expire in the next 90 days. Start renewal discussions.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnershipAnalytics;